import { ExpandLess, ExpandMore } from '@mui/icons-material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import {
  Divider,
  Grid,
  Skeleton,
  Typography,
  useTheme,
  useMediaQuery,
  Stack,
} from '@mui/material';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardActions from '@mui/material/CardActions';
import CardContent from '@mui/material/CardContent';
import Collapse from '@mui/material/Collapse';
import IconButton from '@mui/material/IconButton';
import { styled } from '@mui/material/styles';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import * as React from 'react';
import useInfiniteScroll from 'react-infinite-scroll-hook';
import { AscendingIcon, DescendingIcon } from './Icons/SortIcons';

const StyledDiv = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  background: '#fff',
  borderRadius: '15px',
  height: '30px',
  padding: '4px',
  justifyContent: 'center',
  boxShadow: '0px 0px 4px 0px rgba(0, 0, 0, 0.25)',
}));

const Row = ({ row, columns, collpasibleGetter, onClick, handleApi }) => {
  const [open, setOpen] = React.useState(false);
  const colSpan = columns.length + 1;

  const onClickHandle = () => {
    !!onClick && onClick(row);
  };

  const sxTableRow = {
    backgroundColor: theme => theme.palette.background.paper,
    borderRight: '1px solid #f0f0f0',
    borderLeft: '1px solid #f0f0f0',
  };

  return (
    <React.Fragment>
      <TableRow
        sx={{
          '& > *': { borderBottom: 'unset !important' },
          ':hover': { cursor: onClick && 'pointer' },
          ...sxTableRow,
        }}
        onClick={onClickHandle}
        hover={!!onClick}
      >
        <TableCell>
          <IconButton
            aria-label="expand row"
            size="small"
            onClick={event => {
              event.stopPropagation();
              const willOpen = !open;
              setOpen(willOpen);
              if (willOpen && row?.vehicle?.showPhotoCount) {
                handleApi(row);
              }
            }}
          >
            {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
          </IconButton>
        </TableCell>
        {columns?.map((column, index) => (
          <TableCell key={index} align={column.align ?? 'left'}>
            {column.valueGetter ? column.valueGetter(row) : row[column.field]}
          </TableCell>
        ))}
      </TableRow>
      <TableRow sx={{ ...sxTableRow }}>
        <TableCell
          style={{ paddingBottom: 0, paddingTop: 0 }}
          colSpan={colSpan}
        >
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Box sx={{ margin: 1 }}>{collpasibleGetter(row)}</Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </React.Fragment>
  );
};

const RowLoading = ({ columns, forwardedRef }) => {
  return (
    <TableRow
      sx={{
        '& > *': { borderBottom: 'unset' },
        borderLeft: '1px solid #f0f0f0',
        borderRight: '1px solid #f0f0f0',
        backgroundColor: theme => theme.palette.background.paper,
      }}
      ref={forwardedRef}
    >
      <TableCell>
        <Skeleton />
      </TableCell>
      {columns?.map((column, index) => (
        <TableCell key={index}>
          <Skeleton />
        </TableCell>
      ))}
    </TableRow>
  );
};

const CardRow = ({ row, columns, collpasibleGetter, onClick }) => {
  const [open, setOpen] = React.useState(false);
  const isIndicator = field =>
    field === 'status' || field === 'request' || field === 'actions';
  const getExpandIcon = () => {
    return !open ? <ExpandMore /> : <ExpandLess />;
  };
  return (
    <Card sx={{ marginBottom: 2 }}>
      <Card>
        <CardContent onClick={() => !!onClick && onClick(row)}>
          <Grid
            container
            sx={{
              justifyContent: { sm: 'space-between' },
              flexDirection: { xs: 'column', sm: 'row' },
            }}
          >
            <Grid item order={{ xs: 1, sm: 3 }}>
              <Grid
                container
                sx={{
                  flexDirection: { xs: 'row', sm: 'column' },
                  justifyContent: { xs: 'space-between', sm: 'flex-end' },
                }}
                display="flex"
                alignItems="center"
              >
                {columns.map(
                  (column, index) =>
                    isIndicator(column.field) && (
                      <Grid
                        item
                        key={index}
                        gap={1}
                        pb={1}
                        sx={{
                          alignSelf: { xs: 'center', sm: 'flex-end' },
                        }}
                      >
                        {column.valueGetter(row)}
                      </Grid>
                    )
                )}
              </Grid>
            </Grid>
            <Grid item order={{ xs: 2, sm: 2 }}>
              {columns.map(
                (column, index) =>
                  !isIndicator(column.field) && (
                    <Grid
                      item
                      key={index}
                      justifyContent={column.align}
                      display="flex"
                      alignItems="center"
                      gap={1}
                      pb={1}
                    >
                      {column.headerName && (
                        <Typography
                          variant="subtitle2"
                          component="span"
                          fontWeight={'900'}
                        >
                          {`${column.headerName}:`}
                        </Typography>
                      )}
                      <Typography
                        variant="body2"
                        component="span"
                        sx={{ flexGrow: 1 }}
                      >
                        {column.valueGetter
                          ? column.valueGetter(row)
                          : row[column.field]}
                      </Typography>
                    </Grid>
                  )
              )}
            </Grid>
          </Grid>
        </CardContent>
        <Divider />
        <CardActions sx={{ padding: 0 }}>
          <IconButton
            onClick={event => {
              event.stopPropagation();
              setOpen(!open);
            }}
            sx={{
              width: '100%',
              borderRadius: '0px',
              padding: '4px',
            }}
            aria-expanded={open}
            aria-label="show more"
            size="large"
          >
            {getExpandIcon()}
          </IconButton>
        </CardActions>
        <Collapse in={open} timeout="auto" unmountOnExit>
          <Divider />
          {collpasibleGetter(row)}
        </Collapse>
      </Card>
    </Card>
  );
};

const CardRowLoading = ({ columns, forwardedRef }) => {
  return (
    <Card sx={{ marginBottom: 2 }} ref={forwardedRef}>
      <CardContent>
        <Grid container flexDirection="column">
          {columns.map((column, index) => (
            <Grid
              item
              key={index}
              justifyContent={column.align}
              display="flex"
              alignItems="center"
              gap={1}
              pb={1}
            >
              <Skeleton width={'40%'} />
              <Skeleton width={'40%'} />
            </Grid>
          ))}
        </Grid>
      </CardContent>
    </Card>
  );
};

const SmCollapsibleTable = ({
  columns = [],
  rows,
  collpasibleGetter,
  loading = true,
  hasNextPage,
  onLoadMore,
  onClick,
  dividerSection,
  handleApi = () => {},
}) => {
  const [sentryRef] = useInfiniteScroll({
    loading,
    hasNextPage,
    onLoadMore,
  });

  return (
    <>
      {rows.map((row, index) => (
        <React.Fragment key={index}>
          <CardRow
            row={row}
            columns={columns}
            collpasibleGetter={collpasibleGetter}
            onClick={onClick}
            handleApi={handleApi}
          />
          {dividerSection && dividerSection(row, rows[index + 1]) && (
            <Stack pt={1} pb={3}>
              <Divider />
            </Stack>
          )}
        </React.Fragment>
      ))}
      {(loading || hasNextPage) && (
        <>
          <CardRowLoading columns={columns} forwardedRef={sentryRef} />
        </>
      )}
    </>
  );
};

const LgCollapsibleTable = ({
  columns = [],
  rows,
  collpasibleGetter,
  size = 'medium',
  loading = true,
  hasNextPage,
  onLoadMore,
  onClick,
  dividerSection,
  handleFieldSort,
  sortFields,
  handleApi = () => {},
}) => {
  const [sentryRef] = useInfiniteScroll({
    loading,
    hasNextPage,
    onLoadMore,
  });

  const handleColumnSort = (field, sortBy) => {
    handleFieldSort(field, sortBy);
  };

  return (
    <TableContainer sx={{ height: '100%' }}>
      <Table aria-label="collapsible table" size={size} sx={{ height: '100%' }}>
        <TableHead sx={{ height: '100%' }}>
          <TableRow
            sx={{
              border: '1px solid #f0f0f0',
              backgroundColor: theme => theme.palette.background.paper,
              height: '100%',
            }}
          >
            <TableCell />
            {columns?.map((column, index) => {
              return (
                <TableCell key={index}>
                  <div
                    style={{
                      display: column?.isSortEnabled ? 'flex' : 'table-cell',
                      gap: column?.isSortEnabled ? '5px' : '0px',
                      height: column?.isSortEnabled ? '100%' : 'auto',
                      alignItems: column?.isSortEnabled ? 'center' : 'initial',
                    }}
                  >
                    {column.headerName}
                    {column?.isSortEnabled && (
                      <StyledDiv>
                        <IconButton
                          aria-label="sort column in ascending order"
                          size="small"
                          sx={{ padding: '0px', margin: '1px' }}
                          onClick={() => handleColumnSort(column.field, 'ASC')}
                        >
                          <AscendingIcon
                            backgroundColor={
                              sortFields?.[column?.field]?.['asc']
                                ? '#007DFF'
                                : '#212121'
                            }
                          />
                        </IconButton>
                        <IconButton
                          size="small"
                          aria-label="sort column in descending order"
                          sx={{ padding: '0px', margin: '1px' }}
                          onClick={() => handleColumnSort(column.field, 'DESC')}
                        >
                          <DescendingIcon
                            backgroundColor={
                              sortFields?.[column?.field]?.['desc']
                                ? '#007DFF'
                                : '#212121'
                            }
                          />
                        </IconButton>
                      </StyledDiv>
                    )}
                  </div>
                </TableCell>
              );
            })}
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row, index) => (
            <React.Fragment key={index}>
              <Row
                columns={columns}
                row={row}
                collpasibleGetter={collpasibleGetter}
                onClick={onClick}
                handleApi={handleApi}
              />
              {dividerSection?.(row, rows[index + 1]) && (
                <TableRow>
                  <TableCell colSpan={columns.length + 1} sx={{ px: 0, py: 3 }}>
                    <Divider />
                  </TableCell>
                </TableRow>
              )}
            </React.Fragment>
          ))}
          {(loading || hasNextPage) && (
            <>
              <RowLoading columns={columns} forwardedRef={sentryRef} />
              {[...new Array(4)].map((item, index) => (
                <RowLoading key={index} columns={columns} />
              ))}
            </>
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default function CollapsibleTable({
  columns = [],
  rows,
  collpasibleGetter,
  size = 'medium',
  loading = true,
  hasNextPage,
  onLoadMore,
  onClick,
  dividerSection,
  handleFieldSort,
  sortFields,
  sortColumnBy,
  handleApi = () => {},
}) {
  const theme = useTheme();
  const isMediumSizeBreakPoint = useMediaQuery(theme.breakpoints.down('md'));

  return isMediumSizeBreakPoint ? (
    <SmCollapsibleTable
      columns={columns}
      rows={rows}
      collpasibleGetter={collpasibleGetter}
      loading={loading}
      hasNextPage={hasNextPage}
      onLoadMore={onLoadMore}
      onClick={onClick}
      dividerSection={dividerSection}
      handleFieldSort={handleFieldSort}
      handleApi={handleApi}
    />
  ) : (
    <LgCollapsibleTable
      columns={columns}
      rows={rows}
      collpasibleGetter={collpasibleGetter}
      size={size}
      loading={loading}
      hasNextPage={hasNextPage}
      onLoadMore={onLoadMore}
      onClick={onClick}
      dividerSection={dividerSection}
      handleFieldSort={handleFieldSort}
      sortFields={sortFields}
      sortColumnBy={sortColumnBy}
      handleApi={handleApi}
    />
  );
}
